import { addMinutes } from "date-fns";
import { isEqual } from "lodash";
import { SagaIterator } from "redux-saga";
import { all, call, fork, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import { dateRangeOptions } from "components/filters/date-range-filter/date-range-filter";
import { CustomDateRangeFilterId, Filter, IDateRangeWithCompareFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { Last7DaysMode } from "constants/reports/last-7-days-mode";
import { ReportExportType, ReportType } from "constants/reports/report-type";
import Role from "constants/role";
import { ToastContent, getToastContent } from "constants/toasts";
import * as analytics from "helpers/analytics-reports";
import { getFiltersByReportType } from "helpers/reports";
import { navigate } from "helpers/routing";
import { ApiManager } from "services/api";
import { RequestAction } from "store/entities/actions";
import { selectLoggedInAgent, selectLoggedInAgentLogin } from "store/entities/agents";
import { selectAvailableSources } from "store/entities/license";
import {
  CreateReportActionNames,
  DeleteScheduledReportActionNames,
  EditScheduledReportActionNames,
  REPORT,
  ReportActions,
} from "store/entities/reports/actions";
import {
  ICreateReportPayload,
  IFetchReportFailurePayload,
  IFetchReportSuccessPayload,
} from "store/entities/reports/interfaces";
import { TagActions } from "store/entities/tags";
import { SessionActionNames } from "store/features/session";
import { ToastsActions } from "store/features/toasts/actions";
import { ToastAutoHideDelay, ToastVariant } from "store/features/toasts/interfaces";
import { UrlQueryParamsFeatureActionNames } from "store/features/url-query-params/actions";
import {
  IUrlQueryParamsFeatureRegisterViewPayload,
  UrlQueryParamsView,
} from "store/features/url-query-params/interfaces";
import { IActionWithPayload } from "store/helper";

import { ViewActionSource } from "../view-actions";

import { ReportsViewActions, ReportsViewActionsNames } from "./actions";
import reportDataDeserializers from "./data-deserializers";
import { heatmapDeserializer } from "./data-deserializers/heatmap-deserializer";
import {
  deserializeScheduledReportCreate,
  deserializeScheduledReportEdit,
} from "./data-deserializers/scheduled-reports-deserializer";
import { mapFetchFailureReasonToMessage, parseChartData } from "./helpers";
import {
  IFetchDataPayload,
  IReportCreatePayloadData,
  IReportsViewFilters,
  IReportsViewSetReportDistributionPayload,
  IReportsViewUpdateFilterPayload,
  IScheduledReportDeleteData,
  IScheduledReportEditData,
  ReportFetchFailureReason,
} from "./interfaces";
import {
  getAllReportsCacheExpirationTime,
  getCurrentView,
  getDistribution,
  getFilters,
  getLast7DaysMode,
  getReportCacheExpirationTime,
  selectIsHeatmapEnabled,
  selectLastAvailableSources,
} from "./selectors";

const reportsHeatmaps = {
  [ReportType.NewTickets]: ReportType.NewTicketsHeatmap,
};

function hasHeatmap(reportType: ReportType): boolean {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return !!reportsHeatmaps[reportType];
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* watchForReportsViewRegistered() {
  while (true) {
    const registerViewAction: IActionWithPayload<string, IUrlQueryParamsFeatureRegisterViewPayload> = yield take(
      UrlQueryParamsFeatureActionNames.REGISTER_VIEW
    );
    if (registerViewAction.payload.view === UrlQueryParamsView.Reports) {
      yield put(ReportsViewActions.inited());
      break;
    }
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateReportViewData(action: IActionWithPayload<string, IFetchReportSuccessPayload>): SagaIterator {
  const { payload } = action;

  if (payload.source === ViewActionSource.Reports) {
    let { data } = payload;

    if (Object.values(reportsHeatmaps).includes(payload.reportType)) {
      // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
      data = yield heatmapDeserializer(data, payload.reportType);
    }

    const distribution = yield select(getDistribution);
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    if (reportDataDeserializers[payload.reportType]) {
      if (Array.isArray(data)) {
        const availableSources = yield select(selectAvailableSources);
        const dataSets = yield all(
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          data.map(dataSet => reportDataDeserializers[payload.reportType](dataSet, distribution, availableSources))
        );

        data = parseChartData(
          data.map((dataSet, index) => ({
            ...dataSet,
            id: index,
            data: dataSets[index],
          }))
        );
      } else {
        // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
        data = yield reportDataDeserializers[payload.reportType]({ data });
      }
    }

    yield put(ReportsViewActions.setReportData(payload.reportType, data));
    yield put(ReportsViewActions.setCacheExpirationTime(payload.reportType, addMinutes(new Date(), 10)));
  }
}

const cacheActiveActions = [ReportsViewActionsNames.SET_CURRENT_VIEW, ReportsViewActionsNames.FETCH_DATA];

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* fetchReport(action: IActionWithPayload<ReportsViewActionsNames, IFetchDataPayload>): SagaIterator {
  const { payload } = action;

  /**
   * currentView doesn't work for HomePage so when content switcher is changed
   * we are setting currentView for Last7Days
   */
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const reportType = yield payload.reportType ||
    (action.type === ReportsViewActionsNames.SET_LAST_7_DAYS_MODE && ReportType.Last7days) ||
    select(getCurrentView);

  const reportExpirationTime = yield select(getReportCacheExpirationTime, reportType);
  const availableSources = yield select(selectAvailableSources);
  const lastAvailableSources = yield select(selectLastAvailableSources);

  const isReportCached = Boolean(
    cacheActiveActions.includes(action.type) && reportExpirationTime && reportExpirationTime > new Date()
  );

  const areLastSourcesEqual = isEqual(availableSources, lastAvailableSources);
  const forceRefetch = reportType === ReportType.NewTickets && !areLastSourcesEqual;

  const shouldRefetch = !isReportCached || forceRefetch;

  if (shouldRefetch && Object.values(ReportType).includes(reportType)) {
    if (!areLastSourcesEqual) {
      yield put(ReportsViewActions.setLastAvailableSources(availableSources));
    }
    const filters = yield select(getFilters);
    const distribution = yield select(getDistribution);

    const reportFilters = getFiltersByReportType(reportType);
    const availableFilters = Object.keys(filters).reduce(
      // @ts-expect-error TS(2769)
      (acc, filterName: Filter) => ({
        ...acc,
        ...(reportFilters.includes(filterName) && { [filterName]: filters[filterName] }),
      }),
      {}
    );

    yield put(
      // @ts-expect-error TS(2769)
      ReportActions.fetch({
        reportType,
        source: ViewActionSource.Reports,
        // @ts-expect-error TS(2559)
        filters: availableFilters,
        distribution,
      })
    );

    const heatmapIsEnabled = yield select(selectIsHeatmapEnabled);
    if (hasHeatmap(reportType) && heatmapIsEnabled) {
      yield put(
        // @ts-expect-error TS(2769)
        ReportActions.fetchHeatMap({
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          reportType: reportsHeatmaps[reportType],
          source: ViewActionSource.Reports,
          // @ts-expect-error TS(2559)
          filters: availableFilters,
          distribution,
        })
      );
    }
  }
}

export function* createReport(action: IActionWithPayload<string, IReportCreatePayloadData>): SagaIterator {
  yield put(
    ReportActions.createReport({
      data: action.payload.data,
      source: ViewActionSource.Reports,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* createReportSuccess(action: IActionWithPayload<string, ICreateReportPayload>) {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const deserializedData = yield action.payload.data ? deserializeScheduledReportCreate(action.payload.data) : null;

  yield put(ReportsViewActions.createReportSuccess(deserializedData));
  if (deserializedData) {
    navigate("/reports/scheduled-reports");
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.REPORT_CREATE_SUCCESS),
        autoHideDelayTime: ToastAutoHideDelay.Long,
        variant: ToastVariant.Success,
      })
    );
  }
}

export function* createReportFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.REPORT_CREATE_ERROR),
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Error,
    })
  );
}

export function* editScheduledReport(action: IActionWithPayload<string, IScheduledReportEditData>): SagaIterator {
  yield put(
    ReportActions.editScheduledReport({
      data: action.payload.data,
      source: ViewActionSource.Reports,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* editScheduledReportSuccess(action: IActionWithPayload<string, IScheduledReportEditData>) {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const deserializedData = yield deserializeScheduledReportEdit(action.payload.data);

  yield put(ReportsViewActions.editScheduledReportSuccess(deserializedData));
  navigate("/reports/scheduled-reports");
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.REPORT_SAVE_SUCCESS),
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Success,
    })
  );
}

export function* editScheduledReportFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.REPORT_SAVE_ERROR),
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Error,
    })
  );
}

export function* deleteScheduledReport(action: IActionWithPayload<string, IScheduledReportDeleteData>): SagaIterator {
  yield put(
    ReportActions.deleteScheduledReport({
      data: { id: action.payload.data.id },
      source: ViewActionSource.Reports,
    })
  );
}

export function* deleteScheduledReportSuccess(
  action: IActionWithPayload<string, IScheduledReportDeleteData>
): SagaIterator {
  const data = { id: action.payload.data.id };
  yield put(ReportsViewActions.deleteScheduledReportSuccess(data));
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.REPORT_DELETE_SUCCESS),
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Success,
    })
  );
}

export function* deleteScheduledReportFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.REPORT_DELETE_ERROR),
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Error,
    })
  );
}

export function* fetchTags(): SagaIterator {
  yield put(TagActions.fetchCollection({}));
}

export function* fetchFailure(action: IActionWithPayload<string, IFetchReportFailurePayload>): SagaIterator {
  const errors = action.payload.errors;

  const error = errors?.reduce<[ReportFetchFailureReason, string] | null>(
    (acc: [ReportFetchFailureReason, string] | null, error: (typeof errors)[number]) => {
      if (acc) return acc;

      const { type, details } = error;

      if (type === "validation") {
        const matchedError = details.errors.find(error =>
          /teamIDs|tagIDs|agentID|maximum date range/.test(error.message)
        );

        if (matchedError) {
          const { message } = matchedError;
          if (/teamIDs/.test(message)) {
            return [ReportFetchFailureReason.TooManyTeams, message];
          } else if (/tagIDs/.test(message)) {
            return [ReportFetchFailureReason.TooManyTags, message];
          } else if (/agentID/.test(message)) {
            return [ReportFetchFailureReason.TooManyAgents, message];
          } else if (/maximum date range/.test(message)) {
            return [ReportFetchFailureReason.DateRangeTooLong, message];
          }
        }
      }

      return acc;
    },
    null
  );

  if (!error) return;

  const [errorType, message] = error;

  const content = mapFetchFailureReasonToMessage(errorType, message);

  yield put(
    ToastsActions.createToast({
      content: content,
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Error,
    })
  );

  const viewFilters = yield select(getFilters);
  switch (errorType) {
    case ReportFetchFailureReason.TooManyTags:
      yield put(
        ReportsViewActions.updateFilter({
          // @ts-expect-error TS(2353)
          name: Filter.Tag,
          value: viewFilters[Filter.Tag].slice(0, viewFilters[Filter.Tag].length - 1),
        })
      );
      break;
    case ReportFetchFailureReason.TooManyTeams:
      yield put(
        ReportsViewActions.updateFilter({
          // @ts-expect-error TS(2353)
          name: Filter.Group,
          value: viewFilters[Filter.Group].groups.slice(0, viewFilters[Filter.Group].groups.length - 1),
        })
      );
      break;
    case ReportFetchFailureReason.TooManyAgents:
      yield put(
        ReportsViewActions.updateFilter({
          // @ts-expect-error TS(2353)
          name: Filter.Agent,
          value: viewFilters[Filter.Agent].agents.slice(0, viewFilters[Filter.Agent].agents.length - 1),
        })
      );
      break;
    case ReportFetchFailureReason.DateRangeTooLong:
      yield put(
        ReportsViewActions.updateFilter({
          // @ts-expect-error TS(2353)
          name: Filter.DateRange,
          value: null,
        })
      );
      break;
    default:
      break;
  }
}

export function* trackDistributionChange(
  action: IActionWithPayload<ReportsViewActionsNames.SET_DISTRIBUTION, IReportsViewSetReportDistributionPayload>
): SagaIterator {
  const { type: distribution } = action.payload;

  const currentReport = yield select(getCurrentView);

  analytics.trackDistributionChange(distribution === ReportDistribution.Hour, currentReport);
}

export function* trackFiltersChange<T extends keyof IReportsViewFilters>(
  action: IActionWithPayload<ReportsViewActionsNames.UPDATE_FILTER, IReportsViewUpdateFilterPayload<T>>
): SagaIterator {
  const { name, compare } = action.payload;

  const currentReport = yield select(getCurrentView);

  const filters = yield select(getFilters);
  const availableFilters = getFiltersByReportType(currentReport);
  const notEmptyFilters = Object.keys(filters).filter(
    filterName => availableFilters.includes(filterName as Filter) && filters[filterName]
  );

  let comparison;

  if (compare) {
    comparison = name;

    if (name === Filter.DateRange) {
      comparison = (compare as IDateRangeWithCompareFilter).id;
    }
  }

  analytics.trackFiltersChange(currentReport, name, notEmptyFilters.length, comparison);
}

export function* setupLast7DaysMode(): SagaIterator {
  const agent = yield select(selectLoggedInAgent);

  if (agent.permission === Role.Normal) {
    yield put(ReportsViewActions.setLast7DaysMode(Last7DaysMode.Agent));
  } else {
    yield put(ReportsViewActions.setLast7DaysMode(Last7DaysMode.License));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* setLast7DaysFilters(action: any): SagaIterator {
  const { resetFilters } = action.payload;

  const agentEmail = yield select(selectLoggedInAgentLogin);
  const last7DaysMode = yield select(getLast7DaysMode);
  const option = dateRangeOptions.find(({ id }) => id === CustomDateRangeFilterId.Last7Days);

  yield put(
    ReportsViewActions.updateFilter({
      // @ts-expect-error TS(2353)
      name: Filter.DateRange,
      value: {
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        id: option.id as CustomDateRangeFilterId,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        ...option.value,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        ...option.relativeValue,
      },
    })
  );

  yield put(
    ReportsViewActions.updateFilter({
      // @ts-expect-error TS(2353)
      name: Filter.Agent,
      value: last7DaysMode === Last7DaysMode.Agent && !resetFilters ? [agentEmail] : null,
    })
  );
}

export function* cleanCaches(action: IActionWithPayload<string, never>): SagaIterator {
  const cacheExpirationTime = yield select(getAllReportsCacheExpirationTime);

  /**
   * Clean cache expiration time rules:
   * - if report isn't Last7Days (have no filters and distribution) or it is but only on mode change (content switcher)
   */
  const reportsToClean = Object.keys(cacheExpirationTime).filter(
    key =>
      (action.type === ReportsViewActionsNames.SET_LAST_7_DAYS_MODE && key === ReportType.Last7days) ||
      key !== ReportType.Last7days
  );

  yield all(
    // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    reportsToClean.map(reportType => put(ReportsViewActions.setCacheExpirationTime(reportType as ReportType, null)))
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* generateReport(
  action: IActionWithPayload<string, { type: ReportExportType; from: Date; to: Date; recipients: string[] }>
) {
  const { type, from, to, recipients } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.reportApi.generateReport, {
    type: type,
    dateFrom: from,
    dateTo: to,
    recipients,
  });

  if (response.error) {
    const isLimitExceeded = response.error.error.type === "limitExceeded";
    const isReportInProgress = !isLimitExceeded && response.responseStatus === 409;
    let reason;
    let limitReason;

    if (isLimitExceeded) {
      reason = "limitExceeded";
      limitReason = response.error.error.message;
    } else if (isReportInProgress) {
      reason = "reportInProgress";
    } else {
      reason = "invalidRange";
    }

    yield put(ReportsViewActions.generateReportFailure(reason, limitReason));
  } else {
    yield put(ReportsViewActions.generateReportSuccess());
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* generateReportSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.GENERATE_REPORT_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* generateReportFailure(action: IActionWithPayload<string, { reason: string; limitExceededReason?: string }>) {
  const { reason, limitExceededReason } = action.payload;

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.GENERATE_REPORT_FAILURE, { reason, limitReason: limitExceededReason }),
      variant: ToastVariant.Error,
      autoHideDelayTime: reason === "limitExceeded" ? ToastAutoHideDelay.Infinite : ToastAutoHideDelay.Long,
      // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      error: { type: reason, message: limitExceededReason },
    })
  );
}

export default function* reportsViewSagas(): SagaIterator {
  yield all([
    fork(watchForReportsViewRegistered),
    takeLatest(
      [
        ReportsViewActionsNames.SET_CURRENT_VIEW,
        ReportsViewActionsNames.SET_DISTRIBUTION,
        ReportsViewActionsNames.UPDATE_FILTER,
        ReportsViewActionsNames.FETCH_DATA,
        ReportsViewActionsNames.SET_LAST_7_DAYS_MODE,
      ],
      fetchReport
    ),
    takeLatest(
      [
        ReportsViewActionsNames.SET_DISTRIBUTION,
        ReportsViewActionsNames.UPDATE_FILTER,
        ReportsViewActionsNames.SET_LAST_7_DAYS_MODE,
      ],
      cleanCaches
    ),
    takeEvery(ReportsViewActionsNames.SET_DISTRIBUTION, trackDistributionChange),
    takeEvery(ReportsViewActionsNames.UPDATE_FILTER, trackFiltersChange),
    takeEvery(ReportsViewActionsNames.CREATE_REPORT, createReport),
    takeEvery(ReportsViewActionsNames.EDIT_SCHEDULED_REPORT, editScheduledReport),
    takeEvery(ReportsViewActionsNames.DELETE_SCHEDULED_REPORT, deleteScheduledReport),
    takeEvery(ReportsViewActionsNames.FETCH_TAGS, fetchTags),
    takeEvery(
      [
        REPORT[ReportType.NewTickets][RequestAction.FAILURE],
        REPORT[ReportType.TicketRatings][RequestAction.FAILURE],
        REPORT[ReportType.TicketFirstResponseTime][RequestAction.FAILURE],
        REPORT[ReportType.TicketResolutionTime][RequestAction.FAILURE],
        REPORT[ReportType.SolvedAndClosed][RequestAction.FAILURE],
        REPORT[ReportType.HandlingTimeByStatus][RequestAction.FAILURE],
      ],
      fetchFailure
    ),
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(CreateReportActionNames[RequestAction.SUCCESS], createReportSuccess),
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(CreateReportActionNames[RequestAction.FAILURE], createReportFailure),
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(EditScheduledReportActionNames[RequestAction.SUCCESS], editScheduledReportSuccess),
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(EditScheduledReportActionNames[RequestAction.FAILURE], editScheduledReportFailure),
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(DeleteScheduledReportActionNames[RequestAction.SUCCESS], deleteScheduledReportSuccess),
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    takeEvery(DeleteScheduledReportActionNames[RequestAction.FAILURE], deleteScheduledReportFailure),
    ...Object.values(ReportType).map(reportName =>
      takeEvery(REPORT[reportName][RequestAction.SUCCESS], updateReportViewData)
    ),
    takeEvery(SessionActionNames.SAVE_AUTHENTICATION_CREDENTIALS, setupLast7DaysMode),
    takeEvery(ReportsViewActionsNames.SET_LAST_7_DAYS_FILTERS, setLast7DaysFilters),
    takeEvery(ReportsViewActionsNames.GENERATE_REPORT_REQUEST, generateReport),
    takeEvery(ReportsViewActionsNames.GENERATE_REPORT_FAILURE, generateReportFailure),
    takeEvery(ReportsViewActionsNames.GENERATE_REPORT_SUCCESS, generateReportSuccess),
  ]);
}
